import React from 'react';

/**
 * DoneRecentlyPanel — proof of work (Epic 8 · S8.1, Part A.3).
 *
 * Pattern: weather-intelligence/today-ahead/ChangeList.js — a compact list of
 * recently delivered items. Each row: emerald ✓ + title + `when.label`
 * ("Done Aug 25"). Row → ItemDetail (`onOpenItem`). "Show all" when
 * `sample_size > rows` → `portalDrill('done_recently', null, {park_id})`
 * (`onShowAll`). 0 items → title + sentence only, no empty table.
 *
 * Presentational — never imports the data layer (Epic 1). The shell owns
 * `onOpenItem` / `onShowAll`.
 */
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const doneLabel = (item) => {
  if (item?.when?.label) return item.when.label;
  if (item?.done_at) {
    const d = new Date(item.done_at);
    if (!isNaN(d.getTime())) return `Done ${MONTHS[d.getMonth()]} ${d.getDate()}`;
    return `Done ${item.done_at}`;
  }
  return '—';
};

const DoneRecentlyPanel = ({ facts, onOpenItem, onShowAll }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];
  const sampleSize = typeof facts?.sample_size === 'number' ? facts.sample_size : items.length;
  const showAll = sampleSize > items.length || items.length > 0;

  if (items.length === 0) {
    return (
      <section data-testid="panel-done_recently" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Done recently</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Nothing has been delivered yet.</p>
      </section>
    );
  }

  return (
    <section data-testid="panel-done_recently" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Done recently</h3>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.id}>
            <button
              type="button"
              onClick={() => onOpenItem?.(it)}
              className="w-full text-left flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded py-0.5"
            >
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-300 text-xs shrink-0" aria-hidden="true">✓</span>
              <span className="flex-1 min-w-0 truncate font-medium">{it.title}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{doneLabel(it)}</span>
            </button>
          </li>
        ))}
      </ul>
      {showAll && (
        <button
          type="button"
          onClick={onShowAll}
          data-testid="done-show-all"
          className="mt-2 text-sm font-medium text-[#32B1FF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded"
        >
          Show all
        </button>
      )}
    </section>
  );
};

export default DoneRecentlyPanel;
